import { getPointsHistory, SinglePointsHistory } from '@/services/lottery';
import { Modal, Table, Pagination } from '@bfe/moly';
import { ColumnDef } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useRedemptionWallet, useWallet } from '@/hooks/wallet';
import { useFm } from '@web3/base-hooks';
import { DirectionrightIcon } from '@bfe/moly-icons';
import { PointsType } from '@/types';
import { Page } from '@/config/track-config';

const UTC_SUFFIX = ' UTC';

export const PointsHistoryButton = () => {
  const { isBybitWallet, isConnectedWallet } = useRedemptionWallet();

  const fm = useFm();
  const [visible, setVisible] = useState(false);

  const handleClick = () => {
    setVisible(true);
  };

  const handleClose = () => {
    setVisible(false);
  };

  if (!isConnectedWallet || !isBybitWallet) {
    return null;
  }

  return (
    <>
      <div
        className="flex items-center gap-1 cursor-pointer"
        onClick={handleClick}
        data-spm-even="Web3UserClick"
        data-spm-data={JSON.stringify({
          page_name: Page.HomePage,
          button_name: 'Web3Points_DashboardHistory',
        })}
      >
        <div className="text-gray-t2 font-medium">{fm('history')}</div>
        <DirectionrightIcon className="text-gray-t2 w-4 h-4" />
      </div>

      <PointsHistory visible={visible} onCancel={handleClose} />
    </>
  );
};

type PointsHistoryProps = {
  visible: boolean;
  onCancel?: () => void;
};

export const PointsHistory = ({ visible, onCancel }: PointsHistoryProps) => {
  const { walletId, walletType } = useWallet();
  const fm = useFm();
  const [fromId, setFromId] = useState(0);
  const limit = 11; // 请求数据条数
  const dataLimit = limit - 1; // 显示数据条数比请求条数少1
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState<boolean>(false);
  const columns: ColumnDef<SinglePointsHistory>[] = [
    {
      header: fm('task'),
      accessorFn: (row) => ({
        type: row.type,
        time: row.time,
      }),
      cell: ({ getValue }) => {
        const value = getValue<{
          type: SinglePointsHistory['type'];
          time: SinglePointsHistory['time'];
        }>();
        return (
          <div className="flex flex-col gap-1">
            <div className="semi-bold-12">
              {getPointsHistoryTitleFromType(value.type, fm)}
            </div>
            <div className="regular-12">{value.time + UTC_SUFFIX}</div>
          </div>
        );
      },
    },
    {
      accessorKey: 'point',
      header: () => <div className="text-right">{fm('exchangeAmount')}</div>,
      cell: ({ row }) => {
        const value = row.getValue<number>('point');
        const sign = value > 0 ? '+' : '';
        return (
          <div className="text-right">{`${sign}${row.getValue('point')}`}</div>
        );
      },
    },
  ];
  const [data, setData] = useState<SinglePointsHistory[]>([]);
  const fetchData = async (direct) => {
    const params: any = {
      walletId,
      walletType,
      limit,
    };
    if (direct != 0) {
      params.direct = direct;
      params.fromId = direct > 0 ? data[limit - 1]?.id : data[0]?.id;
      setPage(direct > 0 ? page + 1 : page - 1);
    } else {
      params.fromId = 0;
      setPage(1);
    }
    setFromId(params.fromId);

    try {
      setLoading(true);
      const res = await getPointsHistory(params);
      if (res.success) {
        setData(res.result);
        if (res.result.length < limit && direct === -1) {
          fetchData(0);
        }
      }
    } catch (error) {
      // TODO 错误处理
    }
    setLoading(false);
  };

  const showData = useMemo(() => data.slice(0, dataLimit), [data, dataLimit]);

  useEffect(() => {
    if (visible && walletId) {
      fetchData(0);
    } else {
      setData([]);
      setFromId(0);
    }
  }, [visible, walletId]);

  return (
    <Modal
      title={fm('web3PointsTracker')}
      footer={null}
      open={visible}
      onCancel={onCancel}
    >
      <Table
        columns={columns}
        data={showData}
        loading={loading}
        emptyText={fm('noRecords')}
      />
      {(fromId !== 0 || (fromId == 0 && showData.length === dataLimit)) && (
        <div className=" mt-6">
          <Pagination
            align="right"
            isStairs
            prevText={fm('prevPage')}
            nextText={fm('nextPage')}
            disabledPrev={page === 1}
            disabledNext={data.length < limit}
            onChangeStairs={(type) => {
              fetchData(type === 'next' ? 1 : -1);
            }}
          />
        </div>
      )}
    </Modal>
  );
};

const getPointsHistoryTitleFromType = (
  type: SinglePointsHistory['type'],
  fm: (id: string, values?: any) => string
) => {
  switch (type) {
    case PointsType.trade:
      return fm('earnPointsByTrade');
    case PointsType.dex100:
      return fm('dexProTrading');
    case PointsType.createWallet:
      return fm('createOrImportWallet');
    case PointsType.wsot:
      return 'WSOT';
    case PointsType.invite:
      return fm('inviteFriends');
    case PointsType.checkIn:
      return fm('dailySignIn');
    case PointsType.socialmedia:
      return fm('followSocialMedia1');
    case PointsType.tgPoints:
      return 'SpaceX';
    default:
      return '';
  }
};
